import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import VisualForecastTableSmall from "./VisualForecastTableSmall";
import { RosterGraph } from "./visual_components/RosterGraph";
import BatchGuidanceTable from "./BatchGuidanceTable";
import DailyLaborComplianceBadge from "./DailyLaborComplianceBadge";
import ReactToPrint from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_get_actual_schedule,
  API_get_actual_used_schedule,
  API_start_store_actuals_backfill
} from "../../../../constants";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import {
  formatPaidBreakStarts,
  getDailyScheduleHourRange,
} from "../../../../helpers/helpers";
import { get_t_str } from "../../../../helpers/helpers";
import { get_t_str_shortened } from "../../../../helpers/helpers";
import { get_t_str_shortened_max } from "../../../../helpers/helpers";
import { get_t_str_minute_precision } from "../../../../helpers/helpers";
import { process_schedule_data } from "../../../../helpers/helpers";
import { process_used_schedule_data } from "../../../../helpers/helpers";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";
import {
  filterCoveredNoShowRows,
  getMissedShiftReason,
  isManagerCancelledShift,
  MISSED_SHIFT_REASON_MANAGER_CANCELLED,
  getCoveredPublishedKey,
  getCoveredPublishedShifts,
  sumCoveredPublishedHours,
} from "./attendanceCoverage";
import "./VisualDailyReportModal2.css";
import { toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 600,
  minHeight: 400,
  width: "99%",
  height: "99%",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
};

const reportPrintStyles = `
  @page {
    size: landscape;
    margin: 0.4in;
  }

  @media print {
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;



const MISSED_SHIFT_REASON_LABELS = {
  called_off_sick: "Called out sick",
  called_off_other: "Called out other",
  no_show: "No-call no-show",
  [MISSED_SHIFT_REASON_MANAGER_CANCELLED]: "Manager cancelled",
};

const MISSED_SHIFT_REASON_ORDER = [
  "no_show",
  "called_off_sick",
  "called_off_other",
  "manager_cancelled",
];

const getMissedShiftReasonLabel = (reason) =>
  MISSED_SHIFT_REASON_LABELS[reason] || null;


const getMissedShiftSummaryKey = (publishedShift, fallbackKey) => {
  if (!publishedShift) {
    return fallbackKey;
  }
  if (publishedShift.id) {
    return `published-${publishedShift.id}`;
  }
  const employeeId =
    publishedShift.store_team_member?.id || publishedShift.store_team_member || "";
  return [
    "published",
    publishedShift.date || publishedShift.date_start || "",
    employeeId,
    publishedShift.shift_start ?? "",
    publishedShift.shift_end ?? "",
    fallbackKey,
  ].join("-");
};

function VisualDailyReportModal2(props) {
  // View state management for employee history
  const [viewMode, setViewMode] = React.useState('daily'); // 'daily' or 'employee_history'
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);
  const [employeeHistoryData, setEmployeeHistoryData] = React.useState({
    scheduledShifts: [],
    actualShifts: []
  });
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [historyDateRange, setHistoryDateRange] = React.useState(null);
  const [isEditingDateRange, setIsEditingDateRange] = React.useState(false);
  const [tempDateRange, setTempDateRange] = React.useState({ start: '', end: '' });
  const [isRefreshingActuals, setIsRefreshingActuals] = React.useState(false);

  // Handle modal close - reset to daily view
  const handleClose = () => {
    setViewMode('daily');
    props.handleClose();
  };

  // Calculate default date range (2 weeks before current date)
  const calculateHistoryDateRange = () => {
    const endDate = new Date(props.date_str);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 14);

    return {
      start: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      end: endDate.toISOString().split('T')[0]
    };
  };

  // Fetch employee shift history
  const fetchEmployeeHistory = async (employeeId, dateRange) => {
    setLoadingHistory(true);
    const axiosConfig = getDefaultAxiosConfig();

    try {
      const scheduledUrl = `${API_get_actual_schedule}${props.store_id}&date_start=${dateRange.start}&date_end=${dateRange.end}`;
      const actualUrl = `${API_get_actual_used_schedule}${props.store_id}&date_start=${dateRange.start}&date_end=${dateRange.end}`;

      const [scheduledRes, actualRes] = await Promise.all([
        axios.get(scheduledUrl, axiosConfig),
        axios.get(actualUrl, axiosConfig)
      ]);

      // Handle nested response structure for backward compatibility
      let scheduledArray = scheduledRes.data;
      if (scheduledRes.data?.redux?.payload?.actual_schedule_list) {
        scheduledArray = scheduledRes.data.redux.payload.actual_schedule_list;
      }

      let actualArray = actualRes.data;
      if (actualRes.data?.redux?.payload?.actual_used_schedule_list) {
        actualArray = actualRes.data.redux.payload.actual_used_schedule_list;
      }

      // Filter by employee
      const employeeScheduled = (scheduledArray || []).filter(
        shift => shift.store_team_member === employeeId
      );
      const employeeActual = process_used_schedule_data(
        filterCoveredNoShowRows(
          (actualArray || []).filter(
            shift => shift.store_team_member?.id === employeeId
          )
        )
      );

      setEmployeeHistoryData({
        scheduledShifts: employeeScheduled,
        actualShifts: (employeeActual || []).map((shift) => {
          const fallbackDate = shift?.date_start
            ? String(shift.date_start).split("T")[0]
            : dateRange?.start;
          const {
            endValue: shiftEndValue,
            hasEnd: hasShiftEnd,
            startValue: shiftStartValue,
          } = getEffectiveShiftEnd(shift, fallbackDate);
          return {
            ...shift,
            shift_start: shiftStartValue ?? shift.shift_start,
            shift_end: shiftEndValue,
            has_shift_end: hasShiftEnd,
          };
        })
      });
    } catch (error) {
      console.error('Error fetching employee history:', error);
      // TODO: Show error message to user
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle employee name click
  const handleEmployeeClick = (employee) => {
    const dateRange = calculateHistoryDateRange();
    setSelectedEmployee(employee);
    setHistoryDateRange(dateRange);
    setViewMode('employee_history');
    fetchEmployeeHistory(employee.id, dateRange);
  };

  // Handle back to daily view
  const handleBackToDaily = () => {
    setViewMode('daily');
    setSelectedEmployee(null);
    setEmployeeHistoryData({ scheduledShifts: [], actualShifts: [] });
  };

  // Handle edit date range click
  const handleEditDateRange = () => {
    setTempDateRange({
      start: historyDateRange.start,
      end: historyDateRange.end
    });
    setIsEditingDateRange(true);
  };

  // Handle save date range
  const handleSaveDateRange = () => {
    if (tempDateRange.start && tempDateRange.end && selectedEmployee) {
      const newDateRange = {
        start: tempDateRange.start,
        end: tempDateRange.end
      };
      setHistoryDateRange(newDateRange);
      setIsEditingDateRange(false);
      fetchEmployeeHistory(selectedEmployee.id, newDateRange);
    }
  };

  // Handle cancel edit date range
  const handleCancelEditDateRange = () => {
    setIsEditingDateRange(false);
    setTempDateRange({ start: '', end: '' });
  };

  const handleRefreshActuals = async () => {
    if (
      !props.store_id ||
      !props.date_str ||
      !props.pos_provider ||
      isRefreshingActuals
    ) {
      return;
    }

    setIsRefreshingActuals(true);
    try {
      await postRightworkAPI(API_start_store_actuals_backfill, {
        store_id: props.store_id,
        start_date: props.date_str,
        end_date: props.date_str,
      });
      toast.success(
        "Actuals refresh finished successfully.",
        { autoClose: 4000 }
      );
      await props.refreshActualsForDate?.(props.date_str);
    } catch (error) {
      const message =
        error?.response?.data?.system?.message ||
        "Unable to refresh actual labor data.";
      toast.error(message, { autoClose: 4000 });
    } finally {
      setIsRefreshingActuals(false);
    }
  };

  const storeDayStartQhnum = Number.isFinite(Number(props.start_qhnum))
    ? Number(props.start_qhnum)
    : null;
  const storeDayEndQhnum =
    storeDayStartQhnum !== null ? storeDayStartQhnum + 24 : null;

  const isPastDate = (dateStr) => {
    if (!dateStr) {
      return false;
    }
    return dayjs(dateStr).isBefore(dayjs(), "day");
  };

  const getEffectiveShiftEnd = (shift, fallbackDateStr) => {
    const startValue = Number.isFinite(Number(shift?.shift_start))
      ? Number(shift.shift_start)
      : null;
    const rawEnd = shift?.shift_end;
    const hasRawEnd =
      rawEnd !== null &&
      rawEnd !== undefined &&
      rawEnd !== "" &&
      Number.isFinite(Number(rawEnd));
    let endValue = hasRawEnd ? Number(rawEnd) : null;
    const shiftDate = shift?.date_start
      ? String(shift.date_start).split("T")[0]
      : shift?.date
        ? String(shift.date).split("T")[0]
        : fallbackDateStr;
    if (!hasRawEnd && storeDayEndQhnum !== null && isPastDate(shiftDate)) {
      endValue = storeDayEndQhnum;
    }
    if (endValue !== null && startValue !== null && endValue < startValue) {
      endValue += 24;
    }
    return {
      endValue,
      hasEnd: endValue !== null,
      startValue,
      shiftDate,
    };
  };

  // All data and calculations for ACTUAL schedule

  const getStartTime = (shift) => {
    // When both shift_start and shift_end are zero, use published.shift_start if available
    if (
      shift.shift_start === 0 &&
      shift.shift_end === 0 &&
      shift.published &&
      shift.published.shift_start !== undefined
    ) {
      return shift.published.shift_start;
    }
    return shift.shift_start;
  };

  const getUnpaidBreaks = (shift) => {
    if (Array.isArray(shift?.unpaid_breaks) && shift.unpaid_breaks.length > 0) {
      return shift.unpaid_breaks;
    }

    if (shift?.break_length > 0 && typeof shift?.break_start === "number") {
      const unpaidBreaks = [
        {
          break_start: shift.break_start,
          break_length: shift.break_length,
        },
      ];
      if ((shift?.break_length_2 || 0) > 0 && typeof shift?.break_start_2 === "number") {
        unpaidBreaks.push({
          break_start: shift.break_start_2,
          break_length: shift.break_length_2,
        });
      }
      return unpaidBreaks;
    }

    return [];
  };

  const parseNumericValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const getUnpaidBreakDurationHours = (shift) => {
    if (Array.isArray(shift?.unpaid_breaks) && shift.unpaid_breaks.length > 0) {
      return shift.unpaid_breaks.reduce((sum, breakEntry) => {
        const breakLength = parseNumericValue(
          breakEntry?.break_length ?? breakEntry?.break_duration ?? 0
        );
        return sum + breakLength;
      }, 0);
    }

    return parseNumericValue(shift?.total_break || 0) || parseNumericValue(shift?.break_length) + parseNumericValue(shift?.break_length_2);
  };

  const getReportScheduledHours = (shift) => {
    if (!shift || isManagerCancelledShift(shift)) {
      return 0;
    }
    return shift.hours ||
      (shift.shift_end - shift.shift_start - getUnpaidBreakDurationHours(shift));
  };

  const getCoveredPublishedHours = (shift) => {
    return sumCoveredPublishedHours(shift, getReportScheduledHours);
  };

  const formatCoveredPublishedTimeRange = (shift, formatter = get_t_str_shortened) => {
    const covered = getCoveredPublishedShifts(shift);
    if (covered.length === 0) {
      return "--";
    }
    if (covered.length === 1) {
      return `${formatter(covered[0].shift_start)} - ${formatter(covered[0].shift_end)}`;
    }
    const firstShift = covered[0];
    const lastShift = covered[covered.length - 1];
    return `${formatter(firstShift.shift_start)} - ${formatter(lastShift.shift_end)} (${covered.length} shifts)`;
  };

  const getScheduledBreaks = (shift) => {
    const scheduledBreaks = [];
    if ((shift?.break_length || 0) > 0 && typeof shift?.break_start === "number") {
      scheduledBreaks.push({
        break_start: shift.break_start,
        break_length: shift.break_length,
      });
    }
    if ((shift?.break_length_2 || 0) > 0 && typeof shift?.break_start_2 === "number") {
      scheduledBreaks.push({
        break_start: shift.break_start_2,
        break_length: shift.break_length_2,
      });
    }
    return scheduledBreaks;
  };

  const formatScheduledBreakStarts = (shift) => {
    const scheduledBreaks = getScheduledBreaks(shift);
    if (!scheduledBreaks.length) {
      return "--";
    }
    return scheduledBreaks
      .map((breakEntry) => get_t_str_shortened(breakEntry.break_start))
      .join(", ");
  };

  const formatUnpaidBreakStarts = (breaks) => {
    if (!breaks?.length) {
      return "";
    }
    return breaks
      .map((breakEntry) => get_t_str_shortened(breakEntry.break_start))
      .join(", ");
  };

  const schedule_data = process_schedule_data(
    props.actual_schedule,
    props.store_team_member_weekly_info,
    props.role_names,
    props.schedule_day_end_qhnum
  );

  // Initialize the show_non_coverage variable
  let show_non_coverage = false;
  // Check if any shift has exclude_from_labor set to true
  show_non_coverage = schedule_data?.some(
    (shift) => shift.exclude_from_labor === true
  );

  let openShiftCounter = 1;

  // Transform the array
  let transformed_schedule_data = schedule_data?.map((shift) => {
    if (shift.name === "") {
      shift.name = `Open Shift ${openShiftCounter}`;
      openShiftCounter++;
    }
    return shift;
  });

  transformed_schedule_data.sort((a, b) => {
    if (a.shift_start !== b.shift_start) {
      return a.shift_start - b.shift_start;
    }
    return a.shift_length - b.shift_length;
  });

  const stm_members_dict = {};
  const stm_is_minor_dict = {};
  props.store_team_member_weekly_info.forEach((member) => {
    stm_members_dict[member.id] = member.hours;
    stm_is_minor_dict[member.id] = member.is_minor;
  });

  const transformed_schedule_data2 = transformed_schedule_data?.map((item) => {
    const total_weekly_hours = stm_members_dict[item.store_team_member];
    const is_minor = stm_is_minor_dict[item.store_team_member];
    return {
      ...item,
      total_weekly_hours:
        total_weekly_hours !== undefined ? total_weekly_hours : null,
      is_minor: is_minor !== undefined ? is_minor : null,
    };
  });

  const groupedData = transformed_schedule_data2.reduce((acc, item) => {
    if (!acc[item.store_team_member]) {
      acc[item.store_team_member] = {
        store_team_member: item.store_team_member,
        name: item.name,
        total_weekly_hours: item.total_weekly_hours,
        is_minor: item.is_minor,
        shifts: [],
      };
    }

    acc[item.store_team_member].shifts.push({
      shift_start: item.shift_start,
      shift_end: item.shift_end,
      shift_length: item.shift_length,
      break_start: item.break_start,
      break_end: item.break_start + item.break_length,
      break_length: item.break_length,
      break_start_2: item.break_start_2,
      break_end_2:
        typeof item.break_start_2 === "number"
          ? item.break_start_2 + (item.break_length_2 || 0)
          : null,
      break_length_2: item.break_length_2 || 0,
      paid_breaks: item.paid_breaks || [],
      pre_shift: item.pre_shift,
      post_shift: item.post_shift,
      role_id: item.role_id,
      role_type: item.role_type,
      date: item.date,
      hours: item.hours,
      color: item.color,
      note: item.note,
      status: item.status,
      store: item.store,
      exclude_from_labor: item.exclude_from_labor,
    });

    return acc;
  }, {});

  const transformed_schedule_data3 = Object.values(groupedData);

  transformed_schedule_data3.sort((a, b) => {
    const startTimeA = a.shifts[0].shift_start;
    const startTimeB = b.shifts[0].shift_start;
    return startTimeA - startTimeB;
  });

  function formatName(name) {
    // Convert the name to lowercase and split it into words
    const words = name?.toLowerCase().split(" ");

    // Capitalize the first letter of each word
    const formattedName = words?.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );

    // Join the words back together with a space between them
    return formattedName.join(" ");
  }

  // create a ref for your component
  let componentRef = React.useRef();

  // Dynamically calculate start_qhnum and end_qhnum based on data
  const laborMetricsFull = props.earned_toggle
    ? props.actual_intraday_metrics?.["Actual Used Labor"] || []
    : props.daily_intraday_metrics?.["Forecasted Labor"] || [];
  const hoursMetricsFull = props.earned_toggle
    ? props.actual_intraday_metrics?.["Actual Used Hours"] || []
    : props.daily_intraday_metrics?.["Scheduled Hours"] || [];

  const actualScheduleSource = props.actual_schedule || [];
  const actualUsedScheduleSource = props.actual_used_schedule || [];
  const missedShiftStatusSummary = React.useMemo(() => {
    const seenPublishedShiftKeys = new Set();
    const counts = {};

    const addPublishedShift = (publishedShift, fallbackKey) => {
      const reason = getMissedShiftReason(publishedShift);
      if (!reason) {
        return;
      }
      const key = getMissedShiftSummaryKey(publishedShift, fallbackKey);
      if (key && seenPublishedShiftKeys.has(key)) {
        return;
      }
      if (key) {
        seenPublishedShiftKeys.add(key);
      }
      counts[reason] = (counts[reason] || 0) + 1;
    };

    actualUsedScheduleSource.forEach((shift, index) => {
      addPublishedShift(shift?.published, `used-${index}`);
    });
    actualScheduleSource.forEach((shift, index) => {
      addPublishedShift(shift?.publishedschedule || shift, `scheduled-${index}`);
    });

    return MISSED_SHIFT_REASON_ORDER
      .filter((reason) => counts[reason] > 0)
      .map((reason) => ({
        reason,
        label: getMissedShiftReasonLabel(reason),
        count: counts[reason],
      }));
  }, [actualScheduleSource, actualUsedScheduleSource]);

  const renderMissedShiftStatusSummary = () => {
    if (!props.earned_toggle || missedShiftStatusSummary.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-700">
          Flagged attendance
        </span>
        {missedShiftStatusSummary.map((item) => (
          <span
            key={item.reason}
            className="inline-flex items-center rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700"
          >
            {item.label}: {item.count}
          </span>
        ))}
      </div>
    );
  };

  const schedule_source = props.earned_toggle
    ? [...actualUsedScheduleSource, ...actualScheduleSource]
    : actualScheduleSource;
  const scheduleWindowSource = schedule_source.filter(
    (d) => !(d.shift_start === 0 && d.shift_end === 0)
  );
  const operatingHoursRow =
    (Array.isArray(props.day_operating_hours) && props.day_operating_hours[0]) ||
    (Array.isArray(props.dow_default_operating_hours) &&
      props.dow_default_operating_hours[0]);
  const operatingHoursOpen = Number(operatingHoursRow?.open_time);
  const operatingHoursClose = Number(operatingHoursRow?.close_time);
  const hasOperatingHoursRow =
    Number.isFinite(operatingHoursOpen) &&
    Number.isFinite(operatingHoursClose) &&
    operatingHoursClose > operatingHoursOpen;

  const scheduleQhNums = scheduleWindowSource
    .flatMap((d) => {
      const { startValue, endValue } = getEffectiveShiftEnd(d, props.date_str);
      if (startValue === null && endValue === null) {
        return [];
      }
      const values = [];
      if (startValue !== null) {
        values.push(startValue);
      }
      if (endValue !== null) {
        values.push(endValue);
      }
      return values;
    })
    .filter((v) => !isNaN(v)); // Only filter NaN, keep 0 (valid midnight)

  let start_qhnum = 8;
  let end_qhnum = 22;

  if (hasOperatingHoursRow) {
    const operatingHourRange = getDailyScheduleHourRange(
      props.dow_default_operating_hours,
      props.day_operating_hours,
      scheduleWindowSource,
      0,
      24,
      (props.earned_toggle
        ? props.calculated_earned_labor_day
        : props.forecasted_labor_day
      )?.filter((row) => Number(row.hours) > 0 || Number(row.labor) > 0)
    );
    if (operatingHourRange.end_h > operatingHourRange.start_h) {
      start_qhnum = operatingHourRange.start_h;
      end_qhnum = operatingHourRange.end_h;
    }
  }

  if (scheduleQhNums.length > 0) {
    if (hasOperatingHoursRow) {
      start_qhnum = Math.min(
        start_qhnum,
        Math.floor(Math.min(...scheduleQhNums))
      );
      end_qhnum = Math.max(end_qhnum, Math.ceil(Math.max(...scheduleQhNums)));
    } else {
      start_qhnum = Math.floor(Math.min(...scheduleQhNums));
      end_qhnum = Math.ceil(Math.max(...scheduleQhNums));
    }
  }

  const forecastedLaborDailySource = props.earned_toggle
    ? props.calculated_earned_labor_day
    : props.forecasted_labor_day;
  const forecastedLaborQhNums = Array.isArray(forecastedLaborDailySource)
    ? forecastedLaborDailySource
        .filter(
          (entry) =>
            Number(entry?.hours) > 0 &&
            Number.isFinite(Number(entry?.qh_num))
        )
        .map((entry) => Number(entry.qh_num))
    : [];

  if (forecastedLaborQhNums.length > 0) {
    start_qhnum = Math.min(
      start_qhnum,
      Math.floor(Math.min(...forecastedLaborQhNums))
    );
    end_qhnum = Math.max(
      end_qhnum,
      Math.ceil(Math.max(...forecastedLaborQhNums))
    );
  }

  const numOfHourBoxes = end_qhnum - start_qhnum;
  const widthOfEachHourBox = 100 / numOfHourBoxes;
  const widthOfEachHalfTick = widthOfEachHourBox / 2;
  const widthOfEachQuarterTick = widthOfEachHourBox / 4;

  const metricsData = props.earned_toggle
    ? props.actual_intraday_metrics
    : props.daily_intraday_metrics;
  const earnedSupportLaborKey = "Earned Support Labor";
  const forecastedSupportLaborKey = "Forecasted Support Labor";
  const derivedMetricsData = React.useMemo(() => {
    if (!metricsData) {
      return metricsData;
    }

    if (!props.earned_toggle) {
      if (Array.isArray(metricsData[forecastedSupportLaborKey])) {
        return metricsData;
      }
      const forecastedHoursSeries = Array.isArray(metricsData["Forecasted Hours"])
        ? metricsData["Forecasted Hours"]
        : [];
      if (forecastedHoursSeries.length === 0) {
        return metricsData;
      }
      return {
        ...metricsData,
        [forecastedSupportLaborKey]: forecastedHoursSeries.map((entry) => ({
          ...entry,
          total_value: (Number(entry?.total_value) || 0) * 2,
        })),
      };
    }

    if (Array.isArray(metricsData[earnedSupportLaborKey])) {
      return metricsData;
    }
    const earnedHoursSeries = Array.isArray(metricsData["Earned Hours"])
      ? metricsData["Earned Hours"]
      : [];
    if (earnedHoursSeries.length === 0) {
      return metricsData;
    }
    return {
      ...metricsData,
      [earnedSupportLaborKey]: earnedHoursSeries.map((entry) => ({
        ...entry,
        total_value: (Number(entry?.total_value) || 0) * 2,
      })),
    };
  }, [metricsData, props.earned_toggle]);

  const totalSales = derivedMetricsData?.["Sales"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalTransactions = derivedMetricsData?.["Transactions"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalItems = derivedMetricsData?.["Items"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );

  const primaryHoursKey = props.earned_toggle
    ? "Earned Hours"
    : "Forecasted Hours";
  const secondaryHoursKey = props.earned_toggle
    ? "Actual Used Hours"
    : "Scheduled Hours";
  const trainingHoursKey = "Training Hours";
  const adminHoursKey = "Admin Hours";
  const primaryLaborKey = props.earned_toggle
    ? "Earned Labor"
    : "Forecasted Labor";
  const primaryLaborDataKey = props.earned_toggle
    ? (Array.isArray(derivedMetricsData?.[earnedSupportLaborKey])
      ? earnedSupportLaborKey
      : primaryLaborKey)
    : (Array.isArray(derivedMetricsData?.[forecastedSupportLaborKey])
      ? forecastedSupportLaborKey
      : primaryLaborKey);
  const secondaryLaborKey = props.earned_toggle
    ? "Actual Used Labor"
    : "Scheduled Labor";

  const sumHalfHourValues = (metricKey) =>
    (derivedMetricsData?.[metricKey] || []).reduce(
      (acc, obj) => acc + (obj?.total_value || 0),
      0
    );

  const sumDailyMetricHours = (entries) => {
    if (!Array.isArray(entries) || !props.agg_column) {
      return null;
    }
    return props.agg_column(entries, "hours");
  };

  const shouldUseDailyEarnedTotals =
    props.earned_toggle && Array.isArray(props.calculated_earned_labor_day);
  const shouldUseDailyActualTotals =
    props.earned_toggle &&
    Array.isArray(props.actual_used_labor_vs_earned_day);

  const totalPrimaryHours = shouldUseDailyEarnedTotals
    ? sumDailyMetricHours(props.calculated_earned_labor_day) ??
      sumHalfHourValues(primaryHoursKey)
    : sumHalfHourValues(primaryHoursKey);
  const totalSecondaryHours = shouldUseDailyActualTotals
    ? sumDailyMetricHours(props.actual_used_labor_vs_earned_day) ??
      sumHalfHourValues(secondaryHoursKey)
    : sumHalfHourValues(secondaryHoursKey);
  const totalTrainingHours = derivedMetricsData?.[trainingHoursKey]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalAdminHours = derivedMetricsData?.[adminHoursKey]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );

  // Filter metrics and guidance based on qh_num range
  const filteredMetrics = {};
  const buildHalfHourSeries = (metric) => {
    const series = [];
    const metricArray = derivedMetricsData?.[metric] || [];
    for (let h = start_qhnum; h < end_qhnum; h += 0.5) {
      const entry = metricArray.find((i) => Math.abs(i.qh_num - h) < 0.001);
      series.push({ qh_num: h, total_value: entry ? entry.total_value : 0 });
    }
    return series;
  };

  [
    "Sales",
    "Transactions",
    "Items",
    primaryLaborDataKey,
    primaryHoursKey,
    secondaryLaborKey,
    secondaryHoursKey,
    trainingHoursKey,
    adminHoursKey,
  ].forEach((metric) => {
    if (derivedMetricsData?.[metric]) {
      filteredMetrics[metric] = buildHalfHourSeries(metric);
    }
  });

  const filteredBatchGuidance = {};
  Object.keys(props.daily_batch_guidance || {}).forEach((item) => {
    filteredBatchGuidance[item] = props.daily_batch_guidance[item].filter(
      (i) => i.qh_num >= start_qhnum && i.qh_num < end_qhnum
    );
  });

  function convertToK(num) {
    // Divide the number by 1000 to get the value in thousands
    let result = num / 1000;

    // Round the result to one decimal place
    result = Math.round(result * 10) / 10;

    // Return the result followed by 'k'
    return result + "k";
  }

  const showRestBreakStarts = props.printHoursShownEnabled === false;

  const renderActualScheduleTable = () => {
    return (
      <>
        <div className="border border-slate-900">
          <div className="w-full border-b border-slate-900 flex">
            <div className="w-[38%] flex py-2 border-r border-slate-900">
              <div className={`${showRestBreakStarts ? "w-[21%]" : "w-[28%]"} order-1 flex items-center px-4`}>
                <p className="text-slate-900 text-xs font-bold">Name</p>
              </div>
              <div className={`${showRestBreakStarts ? "w-[25%]" : "w-[30%]"} order-2 text-center flex items-center justify-center`}>
                <p className="text-slate-900 text-xs font-bold">Scheduled</p>
              </div>
              <div className={`${showRestBreakStarts ? "w-[18%] order-4 px-2" : "w-[12%] order-3"} text-center flex items-center justify-center`}>
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  {showRestBreakStarts ? (
                    <>
                      <span className="whitespace-nowrap">Meal Break</span>
                      <br />
                      Start
                    </>
                  ) : "Break Start"}
                </p>
              </div>
              <div className={`${showRestBreakStarts ? "w-[12%] order-3 px-2" : "w-[16%] order-4"} text-center flex items-center justify-center`}>
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  Shift Hours
                </p>
              </div>
              <div className={`${showRestBreakStarts ? "w-[24%] px-2" : "w-[14%]"} order-5 text-center flex items-center justify-center`}>
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  {showRestBreakStarts ? (
                    <>
                      <span className="whitespace-nowrap">Rest Break</span>
                      <br />
                      Start
                    </>
                  ) : "Hours Week"}
                </p>
              </div>
            </div>
            <div className="w-[62%] flex report-right-edge">
              {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                <div
                  key={index}
                  className={`${
                    index < numOfHourBoxes - 1 ? "border-r" : ""
                  } border-slate-900 flex flex-col`}
                  style={{ width: `${widthOfEachHourBox}%` }}
                >
                  <div className="h-[70%] flex items-center justify-center">
                    <p className="text-slate-900 text-xs">
                      {get_t_str_shortened_max(index + start_qhnum)}
                    </p>
                  </div>
                  <div className="h-[30%] flex report-hour-subdivisions">
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {transformed_schedule_data3?.map((employee, index) => (
            <div
              key={index}
              className={`w-full ${
                index !== transformed_schedule_data3.length - 1
                  ? "border-b border-slate-900"
                  : ""
              } ${index % 2 === 0 ? "bg-white" : "bg-slate-100"}`}
            >
              {employee?.shifts?.map((shift, index2) => (
                <div key={`${index2} - 2`} className="w-full flex">
                  <div
                    className={`w-[38%] flex py-3 border-r border-slate-900 relative z-30`}
                  >
                    <div className={`${showRestBreakStarts ? "w-[21%]" : "w-[28%]"} order-1 flex items-center px-4`}>
                      {index2 === 0 && (
                        <div className="flex items-center gap-x-1">
                          <p className="text-slate-900 text-xs">
                            {formatName(employee.name)}
                          </p>
                          {employee.is_minor && (
                            <p className="text-xs font-bold">[m]</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`${showRestBreakStarts ? "w-[25%]" : "w-[30%]"} order-2 text-center flex items-center justify-center`}>
                      <p className="text-slate-900 text-xs">
                        {get_t_str_shortened(shift.shift_start)} -{" "}
                        {get_t_str_shortened(shift.shift_end)}
                      </p>
                    </div>
                    <div className={`${showRestBreakStarts ? "w-[18%] order-4 px-2" : "w-[12%] order-3"} text-center flex items-center justify-center`}>
                      <p className="text-slate-900 text-xs">
                        {formatUnpaidBreakStarts(getUnpaidBreaks(shift))}
                      </p>
                    </div>
                    <div className={`${showRestBreakStarts ? "w-[12%] order-3 px-2" : "w-[16%] order-4"} text-center flex items-center justify-center`}>
                      <p className="text-slate-900 text-xs">
                        {shift.hours?.toFixed(2)}
                      </p>
                    </div>
                    <div className={`${showRestBreakStarts ? "w-[24%] px-2" : "w-[14%]"} order-5 text-center flex items-center justify-center`}>
                      <p className="text-slate-900 text-xs whitespace-normal">
                        {showRestBreakStarts
                          ? formatPaidBreakStarts(shift.paid_breaks)
                          : index2 === 0 && employee.total_weekly_hours?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="w-[62%] flex items-center relative report-right-edge">
                    {/* Shift Fill (white background) */}
                    <div
                      className={`h-7 absolute z-0 ${
                        shift.exclude_from_labor === true
                          ? "bg-diagonal-stripes"
                          : "bg-white"
                      }`}
                      style={{
                        marginLeft: `${
                          shift.shift_start * 4 * widthOfEachQuarterTick -
                          start_qhnum * 4 * widthOfEachQuarterTick
                        }%`,
                        width: `${
                          shift.shift_length * 4 * widthOfEachQuarterTick
                        }%`,
                      }}
                    ></div>
                    {/* Meal Break */}
                    {shift.break_length > 0 && (
                      <div
                        className="bg-slate-300 h-7 absolute z-5"
                        style={{
                          marginLeft: `${
                            shift.break_start * 4 * widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            shift.break_length * 4 * widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    )}
                    {(shift.break_length_2 || 0) > 0 && (
                      <div
                        className="bg-slate-300 h-7 absolute z-5"
                        style={{
                          marginLeft: `${
                            shift.break_start_2 * 4 * widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            shift.break_length_2 * 4 * widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    )}
                    {/* Paid Breaks */}
                    {shift.paid_breaks?.map((paidBreak, paidBreakIndex) => {
                      const paidBreakDuration =
                        paidBreak?.break_duration || paidBreak?.break_length || 0;

                      if (
                        !paidBreak ||
                        paidBreak?.break_start === undefined ||
                        paidBreakDuration <= 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={`paid-break-${index}-${paidBreakIndex}`}
                          className="bg-emerald-100 h-7 absolute z-5 pointer-events-none"
                          style={{
                            marginLeft: `${
                              paidBreak.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              paidBreakDuration * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    })}
                    {/* Shift Outline (border only, on top) */}
                    <div
                      className="border border-slate-900 h-7 absolute z-10 bg-transparent"
                      style={{
                        marginLeft: `${
                          shift.shift_start * 4 * widthOfEachQuarterTick -
                          start_qhnum * 4 * widthOfEachQuarterTick
                        }%`,
                        width: `${
                          shift.shift_length * 4 * widthOfEachQuarterTick
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="w-full flex">
          <div className="w-[38%] py-2 border-r border-slate-300 flex justify-center items-center my-2">
            <div className="flex gap-x-4">
              <p className="text-slate-900 text-xxs font-bold">
                Employee Type:
              </p>
              {/* <p className="text-slate-900 text-xxs">
                <span className="font-bold">[g]</span> Guest
              </p> */}
              <p className="text-slate-900 text-xxs">
                <span className="font-bold">[m]</span> Minor
              </p>
            </div>
          </div>
          <div className="w-[62%] flex justify-center items-center">
            <div className="flex items-center gap-x-6">
              <p className="text-slate-900 text-xxs font-bold">Schedule:</p>
              <div className="flex items-center gap-x-2">
                <div className="w-6 h-6 border border-slate-900"></div>
                <p className="text-slate-900 text-xxs">Coverage</p>
              </div>
              {show_non_coverage && (
                <div className="flex items-center gap-x-2">
                  <div className="w-6 h-6 border border-slate-900 relative bg-diagonal-stripes"></div>
                  <p className="text-slate-900 text-xxs">Non-coverage</p>
                </div>
              )}
              <div className="flex items-center gap-x-2">
                <div className="w-6 h-6 bg-slate-300"></div>
                <p className="text-slate-900 text-xxs">Meal Break</p>
              </div>
              <div className="flex items-center gap-x-2">
                <div className="w-6 h-6 bg-emerald-100"></div>
                <p className="text-slate-900 text-xxs">Paid Break</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // All data and calculations for USED schedule

  const used_schedule_data = process_used_schedule_data(
    filterCoveredNoShowRows(props.actual_used_schedule)
  );

  // Initialize the show_non_coverage variable for the used table
  let show_non_coverage_used = false;
  // Check if any published shift in used schedule has exclude_from_labor set to true
  show_non_coverage_used = used_schedule_data?.some((shift) =>
    getCoveredPublishedShifts(shift).some(
      (published) => published?.role_id?.exclude_from_labor === true
    )
  );

  let transformed_used_schedule_data = used_schedule_data?.sort((a, b) => {
    const startTimeA = getStartTime(a);
    const startTimeB = getStartTime(b);
    return startTimeA - startTimeB;
  });

  const used_stm_members_dict = {};
  const used_stm_is_minor_dict = {};
  props.store_team_member_weekly_info.forEach((member) => {
    used_stm_members_dict[member.id] = member.hours;
    used_stm_is_minor_dict[member.id] = member.is_minor;
  });

  const transformed_used_schedule_data2 = transformed_used_schedule_data?.map(
    (item) => {
      const total_weekly_hours =
        used_stm_members_dict[item.store_team_member.id];
      const is_minor = used_stm_is_minor_dict[item.store_team_member.id];
      return {
        ...item,
        total_weekly_hours:
          total_weekly_hours !== undefined ? total_weekly_hours : null,
        is_minor: is_minor !== undefined ? is_minor : null,
      };
    }
  );

  const groupedUsedData = transformed_used_schedule_data2.reduce(
    (acc, item) => {
      const unpaidBreakDuration = getUnpaidBreakDurationHours(item);
      if (!acc[item.stm_id]) {
        acc[item.stm_id] = {
          stm_id: item.stm_id,
          name: item.name,
          total_weekly_hours: item.total_weekly_hours,
          is_minor: item.is_minor,
          shifts: [],
        };
      }

      const {
        endValue: shiftEndValue,
        hasEnd: hasShiftEnd,
        startValue: shiftStartValue,
      } = getEffectiveShiftEnd(item, props.date_str);
      const shiftStart = shiftStartValue ?? item.shift_start;
      const shiftLength = hasShiftEnd ? shiftEndValue - shiftStart : 0;
      const hoursWorked = hasShiftEnd
        ? shiftLength - unpaidBreakDuration
        : 0;
      const publishedHours = getCoveredPublishedHours(item);

      acc[item.stm_id].shifts.push({
        shift_start: shiftStart,
        shift_end: shiftEndValue,
        shift_length: shiftLength,
        break_start: item.break_start,
        // break_end: item.break_start + item.break_length,
        break_length: unpaidBreakDuration,
        paid_breaks: item.paid_breaks || [],
        unpaid_breaks: item.unpaid_breaks || [],
        // role_id: item.role_id,
        // role_type: item.role_type,
        date: item.date_start,
        hours: hoursWorked,
        result_start: item.result_start,
        result_end: item.result_end,
        // color: item.color,
        // note: item.note,
        // status: item.status,
        // store: item.store,
        // exclude_from_labor: item.exclude_from_labor,
        published: item.published,
        covered_published_schedules: item.covered_published_schedules || [],
        delta: hasShiftEnd ? hoursWorked - publishedHours : null,
        has_shift_end: hasShiftEnd,
      });

      return acc;
    },
    {}
  );

  const transformed_used_schedule_data3 = Object.values(groupedUsedData);

  transformed_used_schedule_data3.sort((a, b) => {
    const startTimeA = getStartTime(a.shifts[0]);
    const startTimeB = getStartTime(b.shifts[0]);
    return startTimeA - startTimeB;
  });
  // Reuse the same axis calculations for the used schedule table so
  // the schedule and metrics share a single time window

  const renderUsedScheduleTable = () => {
    return (
      <>
        <div className="border border-slate-900">
          <div className="w-full border-b border-slate-900 flex">
            <div className="w-[38%] flex py-2 border-r border-slate-900">
              <div className="w-[22%] flex items-center px-4">
                <p className="text-slate-900 text-xs font-bold">Name</p>
              </div>
              <div className="w-[28%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold">Actual Shift</p>
              </div>
              <div className="w-[28%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  Published Shift
                </p>
              </div>
              <div className="w-[22%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  Delta
                </p>
              </div>
            </div>
            <div className="w-[62%] flex report-right-edge">
              {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                <div
                  key={index}
                  className={`${
                    index < numOfHourBoxes - 1 ? "border-r" : ""
                  } border-slate-900 flex flex-col`}
                  style={{ width: `${widthOfEachHourBox}%` }}
                >
                  <div className="h-[70%] flex items-center justify-center">
                    <p className="text-slate-900 text-xs">
                      {get_t_str_shortened_max(index + start_qhnum)}
                    </p>
                  </div>
                  <div className="h-[30%] flex report-hour-subdivisions">
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {transformed_used_schedule_data3?.map((employee, index) => {
            // Track which published shifts have been rendered to avoid duplicates
            // (e.g. when an employee clocks out and back in for the same published shift)
            const seenPublishedKeys = new Set();
            // Aggregate actual hours per published shift so the first row
            // shows a combined delta and subsequent rows hide it.
            // Also track whether any segment is still open (no shift end)
            // so we can suppress the combined delta for in-progress groups.
            const publishedKeyActualHours = {};
            const publishedKeyHasOpenSegment = {};
            employee?.shifts?.forEach((shift) => {
              const key = getCoveredPublishedKey(shift);
              if (key) {
                publishedKeyActualHours[key] = (publishedKeyActualHours[key] || 0) + (shift.hours || 0);
                if (!shift.has_shift_end) {
                  publishedKeyHasOpenSegment[key] = true;
                }
              }
            });
            const showPublishedForShift = employee?.shifts?.map((shift) => {
              const key = getCoveredPublishedKey(shift);
              if (key) {
                if (!seenPublishedKeys.has(key)) {
                  seenPublishedKeys.add(key);
                  return true;
                }
              }
              return false;
            }) || [];
            const shiftCombinedDelta = employee?.shifts?.map((shift, idx) => {
              if (!showPublishedForShift[idx]) return null;
              const key = getCoveredPublishedKey(shift);
              // If any segment for this published shift is still open,
              // suppress the combined delta to avoid understating worked time.
              if (publishedKeyHasOpenSegment[key]) return null;
              const totalActual = publishedKeyActualHours[key] || 0;
              const publishedHours = getCoveredPublishedHours(shift);
              return totalActual - publishedHours;
            }) || [];

            return (
            <div
              key={index}
              className={`w-full ${
                index !== transformed_used_schedule_data3.length - 1
                  ? "border-b border-slate-900"
                  : ""
              } ${index % 2 === 0 ? "bg-white" : "bg-slate-100"}`}
            >
              {employee?.shifts?.map((shift, index2) => (
                <div key={`${index2} - 2`} className="w-full flex">
                  <div
                    className={`w-[38%] flex py-3 border-r border-slate-900 relative z-30`}
                  >
                    <div className="w-[22%] flex items-center px-4">
                      {index2 === 0 && (
                        <div
                          className="flex items-center gap-x-1 cursor-pointer hover:text-violet-700 transition-colors"
                          onClick={() => handleEmployeeClick({
                            id: employee.stm_id,
                            name: employee.name,
                            is_minor: employee.is_minor
                          })}
                        >
                          <p className="text-slate-900 text-xs underline decoration-dotted hover:text-violet-700">
                            {formatName(employee.name)}
                          </p>
                          {employee.is_minor && (
                            <p className="text-xs font-bold">[m]</p>
                          )}
                        </div>
                      )}
                    </div>
                    {(() => {
                      const shiftHasActualTime =
                        shift.shift_start !== 0 || shift.shift_end !== 0;
                      const shiftMissedReason =
                        showPublishedForShift[index2]
                          ? getMissedShiftReason(shift.published)
                          : null;
                      const shiftMissedLabel =
                        getMissedShiftReasonLabel(shiftMissedReason);
                      const showMissedReason =
                        !shiftHasActualTime && Boolean(shiftMissedLabel);

                      return (
                        <div className="w-[28%] text-center flex items-center justify-center">
                          <p
                            className={`text-slate-900 text-xs ${
                              showMissedReason ? "font-semibold" : ""
                            }`}
                          >
                            {shiftHasActualTime
                              ? (shift.has_shift_end
                                  ? `${get_t_str_minute_precision(
                                      shift.shift_start
                                    )} - ${get_t_str_minute_precision(shift.shift_end)}`
                                  : `${get_t_str_minute_precision(
                                      shift.shift_start
                                    )} - --`)
                              : shiftMissedLabel || "--"}
                          </p>
                        </div>
                      );
                    })()}
                    <div className="w-[28%] text-center flex items-center justify-center">
                      {showPublishedForShift[index2] && (
                        <p className="text-slate-900 text-xs">
                          {formatCoveredPublishedTimeRange(shift, get_t_str_minute_precision)}
                        </p>
                      )}
                    </div>
                    <div className="w-[22%] text-center flex items-center justify-center">
                      {showPublishedForShift[index2] ? (
                        <p
                          className={`${
                            shiftCombinedDelta[index2] > 0 ? "text-rose-700" : "text-emerald-700"
                          } text-xs`}
                        >
                          {shiftCombinedDelta[index2] != null && shift.has_shift_end
                            ? `${shiftCombinedDelta[index2] > 0 ? "+" : ""}${shiftCombinedDelta[index2].toFixed(
                                2
                              )}hrs`
                            : "--"}
                        </p>
                      ) : getCoveredPublishedKey(shift) ? null : (
                        <p
                          className={`${
                            shift.delta > 0 ? "text-rose-700" : "text-emerald-700"
                          } text-xs`}
                        >
                          {shift.has_shift_end
                            ? `${shift.delta > 0 ? "+" : ""}${shift.delta.toFixed(
                                2
                              )}hrs`
                            : "--"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="w-[62%] flex items-center relative report-right-edge">
                    {/* Published Shift Fill (white background) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      <div
                        key={`published-fill-${publishedShift.id || publishedShift.shift_start}`}
                        className={`h-7 absolute z-0 ${
                          publishedShift?.role_id?.exclude_from_labor === true
                            ? "bg-diagonal-stripes"
                            : "bg-white"
                        }`}
                        style={{
                          marginLeft: `${
                            publishedShift.shift_start *
                              4 *
                              widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            (publishedShift.shift_length || (publishedShift.shift_end - publishedShift.shift_start)) *
                            4 *
                            widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    ))}
                    {/* Published Meal Break (gray on published shift) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      publishedShift?.break_length > 0 && (
                      <div
                        key={`published-meal-break-${publishedShift.id || publishedShift.shift_start}`}
                        className="bg-slate-300 h-7 absolute z-5"
                        style={{
                          marginLeft: `${
                            publishedShift.break_start * 4 * widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            publishedShift.break_length * 4 * widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                      )
                    ))}
                    {/* Published Shift Outline (border only, on top) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      <div
                        key={`published-outline-${publishedShift.id || publishedShift.shift_start}`}
                        className="border border-slate-900 h-7 absolute z-10 bg-transparent"
                        style={{
                          marginLeft: `${
                            publishedShift.shift_start *
                              4 *
                              widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            (publishedShift.shift_length || (publishedShift.shift_end - publishedShift.shift_start)) *
                            4 *
                            widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    ))}
                    {/* Published Paid Breaks (emerald on published shift) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).flatMap((publishedShift) => (
                      publishedShift?.paid_breaks?.map((paidBreak, paidBreakIndex) => {
                      const paidBreakDuration =
                        paidBreak?.break_duration || paidBreak?.break_length || 0;

                      if (
                        !paidBreak ||
                        paidBreak?.break_start === undefined ||
                        paidBreakDuration <= 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={`published-paid-break-${publishedShift.id || publishedShift.shift_start}-${paidBreakIndex}`}
                          className="bg-emerald-100 h-7 absolute"
                          style={{
                            marginLeft: `${
                              paidBreak.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              paidBreakDuration * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    }) || []
                    ))}
                    {/* Actual Shift Bar (purple) */}
                    <div
                      className="bg-violet-900 opacity-50 h-3 rounded-sm absolute"
                      style={{
                        marginLeft: `${
                          shift.shift_start * 4 * widthOfEachQuarterTick -
                          start_qhnum * 4 * widthOfEachQuarterTick
                        }%`,
                        width: `${
                          shift.shift_length * 4 * widthOfEachQuarterTick
                        }%`,
                      }}
                    ></div>
                    {/* Actual Meal Breaks (solid purple on actual shift) */}
                    {getUnpaidBreaks(shift).map((breakEntry, breakIndex) => {
                      if (
                        breakEntry?.break_start === undefined ||
                        breakEntry?.break_length <= 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={`used-break-${index}-${index2}-${breakIndex}`}
                          className="bg-violet-900 opacity-100 h-3 rounded-sm absolute"
                          style={{
                            marginLeft: `${
                              breakEntry.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              breakEntry.break_length * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    })}
                    {/* Actual Paid Breaks (fuchsia on actual shift) */}
                    {shift.paid_breaks?.map((paidBreak, paidBreakIndex) => {
                      const paidBreakDuration =
                        paidBreak?.break_duration || paidBreak?.break_length || 0;

                      if (
                        !paidBreak ||
                        paidBreak?.break_start === undefined ||
                        paidBreakDuration <= 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={`used-paid-break-${index}-${paidBreakIndex}`}
                          className="bg-fuchsia-400 h-3 rounded-sm absolute"
                          style={{
                            marginLeft: `${
                              paidBreak.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              paidBreakDuration * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )})}
        </div>
        <div className="w-full flex">
          <div className="w-[38%] py-2 border-r border-slate-300 flex justify-center items-center my-2">
            <div className="flex gap-x-4">
              <p className="text-slate-900 text-xxs font-bold">
                Employee Type:
              </p>
              {/* <p className="text-slate-900 text-xxs">
                <span className="font-bold">[g]</span> Guest
              </p> */}
              <p className="text-slate-900 text-xxs">
                <span className="font-bold">[m]</span> Minor
              </p>
            </div>
          </div>
          <div className="w-[62%] flex justify-center items-center">
            <div className="flex items-center gap-x-4 flex-wrap justify-center">
              <p className="text-slate-900 text-xxs font-bold">Actual:</p>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-violet-900 opacity-50"></div>
                <p className="text-slate-900 text-xxs">Shift</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-violet-900"></div>
                <p className="text-slate-900 text-xxs">Meal Break</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-fuchsia-400"></div>
                <p className="text-slate-900 text-xxs">Paid Break</p>
              </div>
              <p className="text-slate-900 text-xxs font-bold ml-2">Published:</p>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 border border-slate-900"></div>
                <p className="text-slate-900 text-xxs">Shift</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-slate-300"></div>
                <p className="text-slate-900 text-xxs">Meal Break</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-emerald-100"></div>
                <p className="text-slate-900 text-xxs">Paid Break</p>
              </div>
              {show_non_coverage_used && (
                <div className="flex items-center gap-x-1">
                  <div className="w-5 h-5 border border-slate-900 relative bg-diagonal-stripes"></div>
                  <p className="text-slate-900 text-xxs">Non-coverage</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderEmployeeHistoryTable = () => {
    if (loadingHistory) {
      return (
        <div className="flex justify-center items-center" style={{ minHeight: '500px' }}>
          <LoadSpinner />
        </div>
      );
    }

    // Group shifts by date
    const shiftsByDate = {};

    // Process scheduled shifts
    employeeHistoryData.scheduledShifts.forEach(shift => {
      const date = shift.date;
      if (!shiftsByDate[date]) {
        shiftsByDate[date] = { scheduled: [], actual: [] };
      }
      shiftsByDate[date].scheduled.push(shift);
    });

    // Process actual shifts
    employeeHistoryData.actualShifts.forEach(shift => {
      const date = shift.date_start?.split('T')[0]; // Extract date from datetime
      if (!shiftsByDate[date]) {
        shiftsByDate[date] = { scheduled: [], actual: [] };
      }
      shiftsByDate[date].actual.push(shift);
    });

    // Sort dates chronologically (newest first)
    const sortedDates = Object.keys(shiftsByDate).sort().reverse();

    // Calculate time axis across all dates
    let globalStartQhnum = 24;
    let globalEndQhnum = 0;

    Object.values(shiftsByDate).forEach(dayData => {
      [...dayData.scheduled, ...dayData.actual].forEach(shift => {
        const startValue = Number.isFinite(Number(shift.shift_start))
          ? Number(shift.shift_start)
          : 0;
        let endValue = null;
        if (
          shift.shift_end !== null &&
          shift.shift_end !== undefined &&
          shift.shift_end !== "" &&
          Number.isFinite(Number(shift.shift_end))
        ) {
          endValue = Number(shift.shift_end);
          if (endValue < startValue) {
            endValue += 24;
          }
        }
        if (startValue > 0 && startValue < globalStartQhnum) globalStartQhnum = startValue;
        if (endValue !== null && endValue > 0 && endValue > globalEndQhnum) globalEndQhnum = endValue;
      });
    });

    globalStartQhnum = Math.floor(globalStartQhnum);
    globalEndQhnum = Math.ceil(globalEndQhnum);
    const numOfHourBoxes = globalEndQhnum - globalStartQhnum;

    // Guard against empty history - if no shifts exist, don't render time axis
    const hasShifts = numOfHourBoxes > 0;
    const widthOfEachHourBox = hasShifts ? 100 / numOfHourBoxes : 0;
    const widthOfEachQuarterTick = hasShifts ? widthOfEachHourBox / 4 : 0;

    return (
      <>
        <div className="mb-4 flex items-center justify-between report-print-control">
          <button
            onClick={handleBackToDaily}
            className="flex items-center gap-x-2 text-violet-700 hover:text-violet-900 font-medium"
          >
            <ArrowBackIcon fontSize="small" />
            <span className="text-sm">Back to After Action Report</span>
          </button>
          <div className="flex items-center gap-x-2">
            {!isEditingDateRange ? (
              <>
                <div className="text-sm text-slate-600">
                  {historyDateRange?.start} to {historyDateRange?.end}
                </div>
                <IconButton
                  size="small"
                  onClick={handleEditDateRange}
                  className="text-slate-600 hover:text-violet-700"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={tempDateRange.start ? dayjs(tempDateRange.start) : null}
                    onChange={(newValue) => setTempDateRange({
                      ...tempDateRange,
                      start: newValue ? newValue.format('YYYY-MM-DD') : ''
                    })}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      width: "160px",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <span className="text-sm text-slate-600">to</span>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    value={tempDateRange.end ? dayjs(tempDateRange.end) : null}
                    onChange={(newValue) => setTempDateRange({
                      ...tempDateRange,
                      end: newValue ? newValue.format('YYYY-MM-DD') : ''
                    })}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                    sx={{
                      width: "160px",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderRadius: "8px",
                          height: "45px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <IconButton
                  size="small"
                  onClick={handleSaveDateRange}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEditDateRange}
                  className="text-rose-600 hover:text-rose-700"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </div>
        </div>

        {/* Period Summary Metrics - Only show when viewing Actual Used Labor */}
        {props.earned_toggle && sortedDates.length > 0 && (() => {
          // Group shifts by date, using published shift from actual when available
          const publishedByDate = {};
          const actualByDate = {};
          const scheduledByDate = {};

          employeeHistoryData.scheduledShifts.forEach(shift => {
            const date = shift.date;
            if (!scheduledByDate[date]) scheduledByDate[date] = [];
            scheduledByDate[date].push(shift);
          });

          employeeHistoryData.actualShifts.forEach(shift => {
            const date = shift.date_start?.split('T')[0];
            if (!actualByDate[date]) actualByDate[date] = [];
            actualByDate[date].push(shift);
          });

          // Build publishedByDate: use actual shifts' published schedules if they exist, otherwise use scheduled shifts
          const allDates = new Set([...Object.keys(scheduledByDate), ...Object.keys(actualByDate)]);
          allDates.forEach(date => {
            const eligibleScheduledShifts = (scheduledByDate[date] || [])
              .filter(shift => !isManagerCancelledShift(shift));
            if (actualByDate[date] && actualByDate[date].length > 0) {
              // If there are actual shifts, use their published schedules
              const publishedSchedules = Array.from(
                new Map(
                  actualByDate[date]
                    .flatMap(getCoveredPublishedShifts)
                    .filter(shift => !isManagerCancelledShift(shift))
                    .map((shift, index) => [
                      shift.id ?? `${shift.shift_start}-${shift.shift_end}-${index}`,
                      shift,
                    ])
                ).values()
              );

              if (publishedSchedules.length > 0) {
                // Use published schedules from actual shifts
                publishedByDate[date] = publishedSchedules;
              } else if (eligibleScheduledShifts.length > 0) {
                // Actual shifts exist but none have published data, fall back to scheduled
                publishedByDate[date] = eligibleScheduledShifts;
              }
              // If no published schedules and no scheduled shifts, this is truly unscheduled
            } else if (eligibleScheduledShifts.length > 0) {
              // No actual shifts, use scheduled shifts
              publishedByDate[date] = eligibleScheduledShifts;
            }
          });

          // Check if any day has more than one shift (either scheduled or actual)
          const hasMultipleShiftsPerDay = Object.keys(scheduledByDate).concat(Object.keys(actualByDate))
            .some(date => {
              const scheduledCount = scheduledByDate[date]?.length || 0;
              const actualCount = actualByDate[date]?.length || 0;

              // If either scheduled or actual has more than 1 shift, return true
              return scheduledCount > 1 || actualCount > 1;
            });

          // Calculate attendance metrics using published shifts
          const shiftsPublished = Object.keys(publishedByDate).length;
          let publishedShiftsWorked = 0;
          let unscheduledShiftsWorked = 0;

          // Count published shifts that were worked
          Object.keys(publishedByDate).forEach(date => {
            if (actualByDate[date]) {
              const hasWorked = actualByDate[date].some(
                shift => shift.shift_start !== 0 || shift.shift_end !== 0
              );
              if (hasWorked) publishedShiftsWorked++;
            }
          });

          // Count unscheduled shifts that were worked
          Object.keys(actualByDate).forEach(date => {
            if (!publishedByDate[date]) {
              const hasWorked = actualByDate[date].some(
                shift => shift.shift_start !== 0 || shift.shift_end !== 0
              );
              if (hasWorked) unscheduledShiftsWorked++;
            }
          });

          const totalShiftsWorked = publishedShiftsWorked + unscheduledShiftsWorked;
          const hasAttendanceRate = shiftsPublished > 0;
          const attendanceRate = hasAttendanceRate
            ? ((publishedShiftsWorked / shiftsPublished) * 100).toFixed(0)
            : null;

          // Calculate hours using published shifts
          let totalPublishedHours = 0;
          Object.keys(publishedByDate).forEach(date => {
            publishedByDate[date].forEach(published => {
              totalPublishedHours += getReportScheduledHours(published);
            });
          });

          const totalActualHours = employeeHistoryData.actualShifts
            .filter(shift => shift.shift_start !== 0 || shift.shift_end !== 0)
            .reduce((sum, shift) => {
              const hasShiftEnd =
                shift.shift_end !== null &&
                shift.shift_end !== undefined &&
                shift.shift_end !== "" &&
                Number.isFinite(Number(shift.shift_end));
              const startValue = Number.isFinite(Number(shift.shift_start))
                ? Number(shift.shift_start)
                : null;
              if (!hasShiftEnd || startValue === null) {
                return sum;
              }
              let endValue = Number(shift.shift_end);
              if (endValue < startValue) {
                endValue += 24;
              }
              return sum + (endValue - startValue - (shift.total_break || 0));
            }, 0);

          const totalDelta = totalActualHours - totalPublishedHours;
          const deltaPercentage = totalPublishedHours > 0
            ? ((totalActualHours / totalPublishedHours - 1) * 100).toFixed(1)
            : 0;

          // Calculate punctuality (average minutes late/early) - only for published shifts
          const punctualityData = [];

          Object.keys(publishedByDate).forEach(date => {
            const dayPublished = publishedByDate[date];
            const dayActual = actualByDate[date] || [];

            // Only include if there was both a published shift and actual shift worked on this date
            if (dayActual.length > 0) {
              // Sort both arrays by shift start time to pair them correctly
              const sortedPublished = [...dayPublished].sort((a, b) =>
                (a.shift_start || 0) - (b.shift_start || 0)
              );
              const sortedActual = [...dayActual].sort((a, b) =>
                (a.shift_start || 0) - (b.shift_start || 0)
              );

              // Pair shifts one-to-one (1st with 1st, 2nd with 2nd, etc.)
              const pairCount = Math.min(sortedPublished.length, sortedActual.length);
              for (let i = 0; i < pairCount; i++) {
                const publishedShift = sortedPublished[i];
                const actualShift = sortedActual[i];

                const actualHasStart = actualShift.shift_start !== null &&
                  actualShift.shift_start !== undefined &&
                  actualShift.shift_start !== "" &&
                  Number.isFinite(Number(actualShift.shift_start));
                if (actualHasStart && publishedShift.shift_start) {
                  const lateness = (Number(actualShift.shift_start) - publishedShift.shift_start) * 60; // Convert to minutes
                  punctualityData.push(lateness);
                }
              }
            }
          });

          const avgPunctuality = punctualityData.length > 0
            ? punctualityData.reduce((sum, val) => sum + val, 0) / punctualityData.length
            : 0;

          // Calculate break compliance (only for shifts that were worked and had scheduled breaks)
          let workedShiftsWithScheduledBreaks = 0;
          let actualBreaksTakenCount = 0;

          Object.keys(publishedByDate).forEach(date => {
            const dayPublished = publishedByDate[date];
            const dayActual = actualByDate[date] || [];

            // Only consider if the shift was actually worked
            const wasWorked = dayActual.some(shift => shift.shift_start !== 0 || shift.shift_end !== 0);

            if (wasWorked) {
              // Sort both arrays by shift start time to pair them correctly
              const sortedPublished = [...dayPublished].sort((a, b) =>
                (a.shift_start || 0) - (b.shift_start || 0)
              );
              const sortedActual = [...dayActual].sort((a, b) =>
                (a.shift_start || 0) - (b.shift_start || 0)
              );

              // Pair shifts one-to-one and check break compliance per pair
              const pairCount = Math.min(sortedPublished.length, sortedActual.length);
              for (let i = 0; i < pairCount; i++) {
                const publishedShift = sortedPublished[i];
                const actualShift = sortedActual[i];

                const scheduledBreaks = getScheduledBreaks(publishedShift);
                if (scheduledBreaks.length > 0) {
                  workedShiftsWithScheduledBreaks += scheduledBreaks.length;
                  const actualBreakCount = Math.max(
                    getUnpaidBreaks(actualShift).length,
                    actualShift.total_break > 0 ? 1 : 0
                  );
                  actualBreaksTakenCount += Math.min(
                    actualBreakCount,
                    scheduledBreaks.length
                  );
                }
              }
            }
          });

          const breakComplianceRate = workedShiftsWithScheduledBreaks > 0
            ? ((actualBreaksTakenCount / workedShiftsWithScheduledBreaks) * 100).toFixed(0)
            : 100;

          return (
            <div className="mb-6 bg-slate-100 border border-slate-300 rounded-lg p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Period Summary</h3>

              {hasMultipleShiftsPerDay ? (
                <div className="text-center py-8 text-slate-600">
                  <p className="text-sm">
                    Period summary metrics are not available when the employee has multiple shifts on the same day within this time period.
                  </p>
                </div>
              ) : (
              <div className="grid grid-cols-5 gap-4">
                {/* Shifts */}
                <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                  <div className="mb-2">
                    <CalendarTodayIcon className="text-slate-600" fontSize="medium" />
                  </div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Shifts</div>
                  <div className="text-lg font-bold text-slate-900">
                    {totalShiftsWorked} of {shiftsPublished}
                  </div>
                  <div className={`text-xs font-semibold mt-1 ${
                    !hasAttendanceRate ? 'text-slate-600' :
                    attendanceRate >= 95 ? 'text-emerald-600' :
                    attendanceRate >= 85 ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {hasAttendanceRate ? `${attendanceRate}% attendance` : 'N/A attendance'}
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                  <div className="mb-2">
                    <AccessTimeIcon className="text-slate-600" fontSize="medium" />
                  </div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Hours</div>
                  <div className="text-lg font-bold text-slate-900">
                    {totalActualHours.toFixed(1)} / {totalPublishedHours.toFixed(1)}
                  </div>
                  <div className={`text-xs font-semibold mt-1 ${
                    totalDelta > 0 ? 'text-rose-600' :
                    totalDelta < 0 ? 'text-emerald-600' :
                    'text-slate-600'
                  }`}>
                    {totalDelta > 0 ? '+' : ''}{totalDelta.toFixed(1)} hrs
                  </div>
                </div>

                {/* Delta Percentage */}
                <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                  <div className="mb-2">
                    <TrendingUpIcon className="text-slate-600" fontSize="medium" />
                  </div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Delta</div>
                  <div className={`text-lg font-bold ${
                    Math.abs(deltaPercentage) <= 5 ? 'text-emerald-600' :
                    Math.abs(deltaPercentage) <= 10 ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {deltaPercentage > 0 ? '+' : ''}{deltaPercentage}%
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {Math.abs(deltaPercentage) <= 5 ? 'On target' :
                     deltaPercentage > 0 ? 'Over scheduled hours' : 'Under scheduled hours'}
                  </div>
                </div>

                {/* Punctuality */}
                <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                  <div className="mb-2">
                    <ScheduleIcon className="text-slate-600" fontSize="medium" />
                  </div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Punctuality</div>
                  <div className={`text-lg font-bold ${
                    Math.abs(avgPunctuality) <= 2 ? 'text-emerald-600' :
                    Math.abs(avgPunctuality) <= 5 ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {avgPunctuality > 0 ? '+' : ''}{avgPunctuality.toFixed(0)} min
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {Math.abs(avgPunctuality) <= 2 ? 'On time' :
                     avgPunctuality > 0 ? 'Avg late' : 'Avg early'}
                  </div>
                </div>

                {/* Break Compliance */}
                <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                  <div className="mb-2">
                    <CheckCircleOutlineIcon className="text-slate-600" fontSize="medium" />
                  </div>
                  <div className="text-xs text-slate-600 font-medium mb-1">Breaks</div>
                  <div className={`text-lg font-bold ${
                    breakComplianceRate >= 95 ? 'text-emerald-600' :
                    breakComplianceRate >= 80 ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {breakComplianceRate}%
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {actualBreaksTakenCount}/{workedShiftsWithScheduledBreaks} breaks taken
                  </div>
                </div>
              </div>
              )}
            </div>
          );
        })()}

        <div className="border border-slate-900">
          {/* Header Row */}
          <div className="w-full border-b border-slate-900 flex">
            <div className="w-[38%] flex py-2 border-r border-slate-900">
              {props.earned_toggle ? (
                <>
                  <div className="w-[22%] flex items-center px-4">
                    <p className="text-slate-900 text-xs font-bold">Date</p>
                  </div>
                  <div className="w-[28%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold">Actual Shift</p>
                  </div>
                  <div className="w-[28%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold w-[85%]">Published Shift</p>
                  </div>
                  <div className="w-[22%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold w-[85%]">Delta</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-[28%] flex items-center px-4">
                    <p className="text-slate-900 text-xs font-bold">Date</p>
                  </div>
                  <div className="w-[30%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold">Scheduled</p>
                  </div>
                  <div className="w-[20%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold w-[85%]">Break Start</p>
                  </div>
                  <div className="w-[22%] text-center flex items-center justify-center">
                    <p className="text-slate-900 text-xs font-bold w-[85%]">Shift Hours</p>
                  </div>
                </>
              )}
            </div>
            <div className="w-[62%] flex report-right-edge">
              {hasShifts && Array.from({ length: numOfHourBoxes }).map((_, index) => (
                <div
                  key={index}
                  className={`${
                    index < numOfHourBoxes - 1 ? "border-r" : ""
                  } border-slate-900 flex flex-col`}
                  style={{ width: `${widthOfEachHourBox}%` }}
                >
                  <div className="h-[70%] flex items-center justify-center">
                    <p className="text-slate-900 text-xs">
                      {get_t_str_shortened_max(index + globalStartQhnum)}
                    </p>
                  </div>
                  <div className="h-[30%] flex report-hour-subdivisions">
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Date Rows */}
          {sortedDates.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No shifts found for {selectedEmployee?.name} in this period
            </div>
          ) : (
            sortedDates.map((date, dateIndex) => {
              const dayData = shiftsByDate[date];

              // Create shift pairs
              const shiftRows = [];

              if (dayData.actual.length > 0) {
                // If there are actual shifts, use them with published schedules or fall back to scheduled
                dayData.actual.forEach((actualShift, index) => {
                  // Try to use published schedule from actual shift, otherwise fall back to scheduled shift
                  let scheduledShift = actualShift.published;
                  if (!scheduledShift && dayData.scheduled.length > 0) {
                    // Fall back to corresponding scheduled shift by index (or first if only one)
                    scheduledShift = dayData.scheduled[Math.min(index, dayData.scheduled.length - 1)];
                  }

                  shiftRows.push({
                    scheduledShift: scheduledShift || null,
                    actualShift: actualShift,
                    sortKey: (scheduledShift?.shift_start || actualShift.shift_start || 0)
                  });
                });
              } else {
                // No actual shifts, so just show scheduled shifts
                dayData.scheduled.forEach((scheduledShift) => {
                  shiftRows.push({
                    scheduledShift: scheduledShift,
                    actualShift: null,
                    sortKey: scheduledShift.shift_start || 0
                  });
                });
              }

              // Sort all rows by start time
              const shiftPairs = shiftRows.sort((a, b) => a.sortKey - b.sortKey);

              return (
                <div
                  key={date}
                  className={`w-full ${
                    dateIndex !== sortedDates.length - 1 ? "border-b border-slate-900" : ""
                  } ${dateIndex % 2 === 0 ? "bg-white" : "bg-slate-100"}`}
                >
                  {shiftPairs.map((pair, shiftIndex) => {
                    const { scheduledShift, actualShift } = pair;

                    // Use published shift from actualShift if available, otherwise use scheduledShift
                    const coveredPublishedShifts = actualShift
                      ? getCoveredPublishedShifts(actualShift)
                      : [];
                    const publishedShift = coveredPublishedShifts[0] || scheduledShift;
                    const displayPublishedShifts =
                      coveredPublishedShifts.length > 0
                        ? coveredPublishedShifts
                        : (publishedShift ? [publishedShift] : []);
                    const missedShiftReason = getMissedShiftReason(publishedShift);
                    const missedShiftLabel = getMissedShiftReasonLabel(missedShiftReason);
                    const actualShiftHasActualTime =
                      actualShift && (actualShift.shift_start !== 0 || actualShift.shift_end !== 0);
                    const scheduledHours = actualShift && coveredPublishedShifts.length > 0
                      ? getCoveredPublishedHours(actualShift)
                      : getReportScheduledHours(publishedShift);
                    const actualShiftHasEnd =
                      actualShift &&
                      actualShift.shift_end !== null &&
                      actualShift.shift_end !== undefined &&
                      actualShift.shift_end !== "" &&
                      Number.isFinite(Number(actualShift.shift_end));
                    const actualShiftStart =
                      actualShift &&
                      actualShift.shift_start !== null &&
                      actualShift.shift_start !== undefined &&
                      actualShift.shift_start !== "" &&
                      Number.isFinite(Number(actualShift.shift_start))
                        ? Number(actualShift.shift_start)
                        : null;
                    let actualShiftEnd = actualShiftHasEnd
                      ? Number(actualShift.shift_end)
                      : null;
                    if (
                      actualShiftHasEnd &&
                      actualShiftStart !== null &&
                      actualShiftEnd < actualShiftStart
                    ) {
                      actualShiftEnd += 24;
                    }
                    const actualHours = actualShift && actualShiftHasEnd && actualShiftStart !== null
                      ? Math.max(
                          actualShiftEnd -
                            actualShiftStart -
                            getUnpaidBreakDurationHours(actualShift),
                          0
                        )
                      : 0;
                    const delta = actualHours - scheduledHours;

                    return (
                      <div key={`${date}-${shiftIndex}`} className="w-full flex">
                    <div className="w-[38%] flex py-3 border-r border-slate-900">
                      {props.earned_toggle ? (
                        <>
                          <div className="w-[22%] flex items-center px-4">
                            {shiftIndex === 0 && (
                              <p className="text-slate-900 text-xs">
                                {formatDateReadableWithDayOfWeek(date)}
                              </p>
                            )}
                          </div>
                          <div className="w-[28%] text-center flex items-center justify-center">
                            <p
                              className={`text-slate-900 text-xs ${
                                !actualShiftHasActualTime && missedShiftLabel
                                  ? "font-semibold"
                                  : ""
                              }`}
                            >
                              {actualShiftHasActualTime
                                ? (actualShiftHasEnd
                                    ? `${get_t_str_minute_precision(actualShift.shift_start)} - ${get_t_str_minute_precision(actualShiftEnd)}`
                                    : `${get_t_str_minute_precision(actualShift.shift_start)} - --`)
                                : missedShiftLabel || '--'
                              }
                            </p>
                          </div>
                          <div className="w-[28%] text-center flex items-center justify-center">
                            <p className="text-slate-900 text-xs">
                              {actualShift && coveredPublishedShifts.length > 0
                                ? formatCoveredPublishedTimeRange(actualShift)
                                : (scheduledShift
                                  ? `${get_t_str_shortened(scheduledShift.shift_start)} - ${get_t_str_shortened(scheduledShift.shift_end)}`
                                  : '--')
                            }
                            </p>
                          </div>
                          <div className="w-[22%] text-center flex items-center justify-center">
                            <p className={`text-xs ${delta > 0 ? 'text-rose-700' : delta < 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                              {publishedShift || (actualShift && (actualShift.shift_start !== 0 || actualShift.shift_end !== 0))
                                ? (actualShiftHasEnd
                                    ? `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`
                                    : '--')
                                : '--'}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-[28%] flex items-center px-4">
                            {shiftIndex === 0 && (
                              <p className="text-slate-900 text-xs">
                                {formatDateReadableWithDayOfWeek(date)}
                              </p>
                            )}
                          </div>
                          <div className="w-[30%] text-center flex items-center justify-center">
                            <p className="text-slate-900 text-xs">
                              {scheduledShift
                                ? `${get_t_str_shortened(scheduledShift.shift_start)} - ${get_t_str_shortened(scheduledShift.shift_end)}`
                                : '--'
                              }
                            </p>
                          </div>
                          <div className="w-[20%] text-center flex items-center justify-center">
                            <p className="text-slate-900 text-xs">
                              {formatScheduledBreakStarts(scheduledShift)}
                            </p>
                          </div>
                          <div className="w-[22%] text-center flex items-center justify-center">
                            <p className="text-slate-900 text-xs">
                              {scheduledHours > 0 ? scheduledHours.toFixed(2) : '--'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Visual Timeline */}
                    <div className="w-[62%] flex items-center relative py-3 report-right-edge">
                      {/* Published/Scheduled shift (white box) - show if published or scheduled exists */}
                      {displayPublishedShifts.map((shiftToRender) => (
                        <div
                          key={`detail-published-${shiftToRender.id || shiftToRender.shift_start}`}
                          className={`border border-slate-900 h-7 absolute ${
                            shiftToRender?.role_id?.exclude_from_labor === true || shiftToRender?.exclude_from_labor === true
                              ? "bg-diagonal-stripes"
                              : "bg-white"
                          }`}
                          style={{
                            marginLeft: `${
                              shiftToRender.shift_start * 4 * widthOfEachQuarterTick -
                              globalStartQhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              (shiftToRender.shift_length || (shiftToRender.shift_end - shiftToRender.shift_start)) * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      ))}

                      {props.earned_toggle && actualShift && (actualShift.shift_start !== 0 || actualShift.shift_end !== 0) ? (
                        <>
                          {/* Actual shift (purple translucent overlay) */}
                          <div
                            className="bg-violet-900 opacity-50 h-3 rounded-sm absolute"
                            style={{
                              marginLeft: `${
                                actualShift.shift_start * 4 * widthOfEachQuarterTick -
                                globalStartQhnum * 4 * widthOfEachQuarterTick
                              }%`,
                              width: `${
                                (actualShiftHasEnd
                                  ? (actualShiftEnd - actualShift.shift_start)
                                  : 0) * 4 * widthOfEachQuarterTick
                              }%`,
                            }}
                          ></div>

                          {/* Actual break (solid purple) */}
                          {getUnpaidBreaks(actualShift).map((breakEntry, breakIndex) => {
                            if (
                              breakEntry?.break_start === undefined ||
                              breakEntry?.break_length <= 0
                            ) {
                              return null;
                            }

                            return (
                              <div
                                key={`history-break-${date}-${shiftIndex}-${breakIndex}`}
                                className="bg-violet-900 opacity-100 h-3 rounded-sm absolute"
                                style={{
                                  marginLeft: `${
                                    breakEntry.break_start * 4 * widthOfEachQuarterTick -
                                    globalStartQhnum * 4 * widthOfEachQuarterTick
                                  }%`,
                                  width: `${
                                    breakEntry.break_length * 4 * widthOfEachQuarterTick
                                  }%`,
                                }}
                              ></div>
                            );
                          })}
                        </>
                      ) : (
                        /* Scheduled break (gray) - only show in scheduled view (not earned_toggle) */
                        !props.earned_toggle &&
                          getScheduledBreaks(scheduledShift).map((breakEntry, breakIndex) => (
                            <div
                              key={`scheduled-break-${date}-${shiftIndex}-${breakIndex}`}
                              className="border border-slate-900 bg-slate-300 h-7 absolute"
                              style={{
                                marginLeft: `${
                                  breakEntry.break_start * 4 * widthOfEachQuarterTick -
                                  globalStartQhnum * 4 * widthOfEachQuarterTick
                                }%`,
                                width: `${breakEntry.break_length * 4 * widthOfEachQuarterTick}%`,
                              }}
                            ></div>
                          ))
                      )}
                    </div>
                  </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

      </>
    );
  };

  return (
    <div>
      <Modal
        className="daily-coverage-modal-root"
        BackdropProps={{ className: "daily-coverage-modal-backdrop" }}
        open={props.open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <Box sx={style} className="daily-coverage-modal-box">
            <div
              className="absolute right-4 top-4 text-slate-500 cursor-pointer"
              onClick={handleClose}
            >
              <CancelIcon />
            </div>
            <div className="p-6 daily-coverage-print-root" ref={componentRef}>
              <div>
                <div className="flex items-center space-x-4">
                  <h2 className="font-bold text-xl">
                    {viewMode === 'employee_history'
                      ? `Shift History: ${formatName(selectedEmployee?.name)}`
                      : (props.earned_toggle
                          ? "After Action Report"
                          : "Planned Action Report")
                    }
                  </h2>
                  {viewMode === 'daily' && (
                    <div className="flex items-center gap-x-2 report-print-control">
                      {props.earned_toggle && props.pos_provider && (
                        <button
                          type="button"
                          onClick={handleRefreshActuals}
                          disabled={isRefreshingActuals}
                          className={`flex items-center gap-x-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                            isRefreshingActuals
                              ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                              : "border-slate-300 text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {isRefreshingActuals ? (
                            <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                          <span>Refresh actuals</span>
                        </button>
                      )}
                      <div className="text-slate-500 cursor-pointer">
                        <ReactToPrint
                          trigger={() => (
                            <IconButton>
                              <PrintIcon />
                            </IconButton>
                          )}
                          content={() => componentRef.current}
                          pageStyle={reportPrintStyles}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <h4 className="mt-1">
                  {viewMode === 'daily' && (
                    <span>{formatDateReadableWithDayOfWeek(props.date_str)}</span>
                  )}
                </h4>
                {viewMode === 'daily' && props.earned_toggle && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                    {renderMissedShiftStatusSummary()}
                    {props.open && props.laborLawEnabled && (
                      <DailyLaborComplianceBadge
                        key={`${props.store_id}-${props.date_str}`}
                        storeId={props.store_id}
                        date={props.date_str}
                        refreshing={isRefreshingActuals}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6">
                {viewMode === 'daily' ? (
                  <>
                    {props.earned_toggle
                      ? renderUsedScheduleTable()
                      : renderActualScheduleTable()}
                  </>
                ) : (
                  renderEmployeeHistoryTable()
                )}
              </div>

              {viewMode === 'daily' && (
                <>
                <div className="border border-slate-900">
                <div className="w-full border-b border-slate-900 flex">
                  <div className="w-[38%] flex py-4 border-r border-slate-900">
                    <div className="w-full flex items-center px-4">
                      <p className="text-slate-900 text-xxs">
                        <span className="font-bold">Metrics </span>
                        <i className="text-xxs">
                          {/* Unfilled shifts are not included in the below metrics */}
                        </i>
                      </p>
                    </div>
                  </div>
                  <div className="w-[62%] flex report-right-edge">
                    {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                      <div
                        key={index}
                        className={`${
                          index < numOfHourBoxes - 1 ? "border-r" : ""
                        } border-slate-900 flex flex-col`}
                        style={{ width: `${widthOfEachHourBox}%` }}
                      >
                        <div className="h-[70%] flex items-center justify-center">
                          <p className="text-slate-900 text-xs">
                            {get_t_str_shortened_max(index + start_qhnum)}
                          </p>
                        </div>
                        <div className="h-[30%] flex report-hour-subdivisions">
                          <div className="w-[50%] border-r border-slate-900"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty("Sales") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          Total Sales
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSales?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          Sales
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={"border-r border-dashed border-slate-200 flex flex-col"}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {value.total_value < 1000
                                  ? Math.round(value.total_value)
                                  : convertToK(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Transactions") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          Total Transactions
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalTransactions)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          Transactions
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Transactions"]?.map(
                        (value, hourIndex) => {
                          return (
                            <div
                              key={hourIndex}
                              className={"border-r border-dashed border-slate-200 flex flex-col"}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Items") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          Total Items
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalItems)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          Items
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Items"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={"border-r border-dashed border-slate-200 flex flex-col"}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {Math.round(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className={`w-full h-5 flex`}>
                  <div
                    className={`w-[38%] flex py-2 border-r border-slate-900`}
                  ></div>
                  <div className="w-[62%] flex report-right-edge">
                    {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={"border-r border-dashed border-slate-200 flex flex-col"}
                          style={{ width: `${widthOfEachHalfTick}%` }}
                        >
                          <div className="h-[100%] flex items-center justify-center"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty(primaryHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {primaryHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalPrimaryHours)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          {primaryLaborKey}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.[primaryLaborDataKey]?.map(
                        (value, hourIndex) => {
                          return (
                            <div
                              key={hourIndex}
                              className={"border-r border-dashed border-slate-200 flex flex-col"}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(secondaryHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {secondaryHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalSecondaryHours)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          {secondaryLaborKey}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.[secondaryLaborKey]?.map(
                        (value, hourIndex) => {
                          return (
                            <div
                              key={hourIndex}
                              className={"border-r border-dashed border-slate-200 flex flex-col"}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(trainingHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {trainingHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalTrainingHours * 100) / 100}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          Training Labor
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.[trainingHoursKey]?.map(
                        (value, hourIndex) => {
                          return (
                            <div
                              key={hourIndex}
                              className={"border-r border-dashed border-slate-200 flex flex-col"}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {Math.round(value.total_value*2 * 100) / 100}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(adminHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {adminHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalAdminHours * 100) / 100}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          Admin Labor
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.[adminHoursKey]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={"border-r border-dashed border-slate-200 flex flex-col"}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {Math.round(value.total_value*2 * 100) / 100}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className={`w-full h-5 flex`}>
                  <div
                    className={`w-[38%] flex py-2 border-r border-slate-900`}
                  ></div>
                  <div className="w-[62%] flex report-right-edge">
                    {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={"border-r border-dashed border-slate-200 flex flex-col"}
                          style={{ width: `${widthOfEachHalfTick}%` }}
                        >
                          <div className="h-[100%] flex items-center justify-center"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty("Sales") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {props.earned_toggle
                            ? "Total Actual SPLH"
                            : "Total Scheduled SPLH"}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          $
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalSales) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          {props.earned_toggle
                            ? "Actual SPLH"
                            : "Scheduled SPLH"}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={"border-r border-dashed border-slate-200 flex flex-col"}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {filteredMetrics?.[secondaryHoursKey]?.[
                                  hourIndex
                                ].total_value !== 0
                                  ? Math.round(
                                      value.total_value /
                                        filteredMetrics?.[secondaryHoursKey]?.[
                                          hourIndex
                                        ].total_value
                                    ) < 1000
                                    ? Math.round(
                                        value.total_value /
                                          filteredMetrics?.[
                                            secondaryHoursKey
                                          ]?.[hourIndex].total_value
                                      )
                                    : convertToK(
                                        Math.round(
                                          value.total_value /
                                            filteredMetrics?.[
                                              secondaryHoursKey
                                            ]?.[hourIndex].total_value
                                        )
                                      )
                                  : 0}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Transactions") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {props.earned_toggle
                            ? "Total Actual TPLH"
                            : "Total Scheduled TPLH"}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalTransactions) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          {props.earned_toggle
                            ? "Actual TPLH"
                            : "Scheduled TPLH"}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Transactions"]?.map(
                        (value, hourIndex) => {
                          return (
                            <div
                              key={hourIndex}
                              className={"border-r border-dashed border-slate-200 flex flex-col"}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {filteredMetrics?.[secondaryHoursKey]?.[
                                    hourIndex
                                  ].total_value !== 0
                                    ? Math.round(
                                        value.total_value /
                                          filteredMetrics?.[
                                            secondaryHoursKey
                                          ]?.[hourIndex].total_value
                                      )
                                    : 0}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Items") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[175px]">
                          {props.earned_toggle
                            ? "Total Actual IPLH"
                            : "Total Scheduled IPLH"}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalItems) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                          {props.earned_toggle
                            ? "Actual IPLH"
                            : "Scheduled IPLH"}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex report-right-edge">
                      {filteredMetrics?.["Items"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={"border-r border-dashed border-slate-200 flex flex-col"}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {filteredMetrics?.[secondaryHoursKey]?.[
                                  hourIndex
                                ].total_value !== 0
                                  ? Math.round(
                                      value.total_value /
                                        filteredMetrics?.[secondaryHoursKey]?.[
                                          hourIndex
                                        ].total_value
                                    )
                                  : 0}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {!props.earned_toggle && (
                <div>
                  {Object.keys(filteredBatchGuidance).length > 0 && (
                    <div className="border border-slate-900 mt-12">
                      <div className="w-full border-b border-slate-900 flex">
                        <div className="w-[38%] flex py-4 border-r border-slate-900">
                          <div className="w-full flex items-center px-4">
                            <p className="text-slate-900 text-xxs">
                              <span className="font-bold">
                                Forecasted Item Quantities{" "}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="w-[62%] flex report-right-edge">
                          {Array.from({ length: numOfHourBoxes }).map(
                            (_, index) => (
                              <div
                                key={index}
                                className={`${
                                  index < numOfHourBoxes - 1 ? "border-r" : ""
                                } border-slate-900 flex flex-col`}
                                style={{ width: `${widthOfEachHourBox}%` }}
                              >
                                <div className="h-[100%] flex items-center justify-center">
                                  <p className="text-slate-900 text-xs">
                                    {get_t_str_shortened_max(
                                      index + start_qhnum
                                    )}
                                  </p>
                                </div>
                                {/* <div className="h-[30%] bg-red-200 flex">
                                <div className="w-[50%] border-r border-slate-900"></div>
                              </div> */}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                      {Object.keys(filteredBatchGuidance).map((item, index) => {
                        const guidance = filteredBatchGuidance[item];
                        const totalSum = guidance.reduce(
                          (acc, obj) => acc + obj.total_value,
                          0
                        );
                        return (
                          <div className={`w-full flex`} key={index}>
                            <div
                              className={`w-[38%] flex py-2 border-r border-slate-900`}
                            >
                              <div className="w-full flex items-center justify-between px-4">
                                <p className="text-slate-900 text-xxs w-[175px]">
                                  Total {item}
                                </p>
                                <p className="text-slate-900 text-xxs">
                                  {Math.round(totalSum)}
                                </p>
                                <p className="text-slate-900 text-xxs w-[175px] flex justify-end">
                                  {item}
                                </p>
                              </div>
                            </div>
                            <div className="w-[62%] flex report-right-edge">
                              {guidance?.map((value, hourIndex) => {
                                return (
                                  <div
                                    key={hourIndex}
                                    className={"border-r border-dashed border-slate-200 flex flex-col"}
                                    style={{ width: `${widthOfEachHourBox}%` }}
                                  >
                                    <div className="h-[100%] flex items-center justify-center">
                                      <p className="text-slate-900 text-xxs">
                                        {Math.round(value.total_value)}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          </Box>
        </div>
      </Modal>
    </div>
  );
}

export default VisualDailyReportModal2;
